import { DateTime, NaiveDate, SemanticTime as _SemanticTime, Utc } from "@tento-chrono";
import { Rational as _Rational } from "@tento-core/rational";
import { Uuid } from "@tento-core/uuid";
import { Field, Model, Scalar } from "@tento-core/model";

export namespace Network {
  export interface DeviceAuthErrorResponse {
    error: string;
    errorDescription: string;
  }
  export interface DevicePollRequest {
    deviceCode: string;
  }
  export type DevicePollStatus = "authorization_pending" | "slow_down" | "expired_token" | "access_denied" | "ok";
  export interface DevicePollResponse {
    apiToken?: Option<string>;
    expiresAt?: Option<string>;
    status: DevicePollStatus;
    tokenType?: Option<string>;
  }
  export interface DeviceStartRequest {
    label?: Option<string>;
    scopes?: Option<string[]>;
    tokenTtlSeconds?: Option<number>;
  }
  export interface DeviceStartResponse {
    deviceCode: string;
    expiresInSeconds: number;
    intervalSeconds: number;
    userCode: string;
    verificationUrl: string;
    verificationUrlComplete?: Option<string>;
  }
  export interface DeviceVerifyRequest {
    userCode: string;
  }
  export interface DeviceVerifyResponse {
    status: string;
  }
  export type ResourceBehavior = {
    File: {
      is_relative: boolean;
      path: string;
    };
  } | {
    Redirect: {
      absolute: boolean;
      location: string;
      subdomain?: Option<string>;
    };
  } | {
    Template: {
      handler: string;
      is_relative: boolean;
      path: string;
    };
  };
  export type ServiceAnonViewerContext = Option<UntypedJson>;
  export type ServiceLoggedInViewerContext = Option<UntypedJson>;
  export type ViewerContextJson = {
    Anon?: Option<ServiceAnonViewerContext>;
  } | {
    User: {
      auth_expired: boolean;
      id: string;
      redirect?: Option<ResourceBehavior>;
    };
  };
  export type ServiceViewerContext = ViewerContextJson;
  export namespace DeviceAuthErrorResponse {
    export const MODEL: Model.Object<DeviceAuthErrorResponse> = Model.object<DeviceAuthErrorResponse>({
      error: Field.required(Scalar.string),
      errorDescription: Field.required(Scalar.string),
    });
    export const hydrate = MODEL.hydrate.bind(MODEL);
    export const safeHydrate = MODEL.safeHydrate.bind(MODEL);
    export const serialize = MODEL.serialize.bind(MODEL);
  }
  export namespace DevicePollRequest {
    export const MODEL: Model.Object<DevicePollRequest> = Model.object<DevicePollRequest>({
      deviceCode: Field.required(Scalar.string),
    });
    export const hydrate = MODEL.hydrate.bind(MODEL);
    export const safeHydrate = MODEL.safeHydrate.bind(MODEL);
    export const serialize = MODEL.serialize.bind(MODEL);
  }
  export namespace DevicePollResponse {
    export const MODEL: Model.Object<DevicePollResponse> = Model.object<DevicePollResponse>({
      apiToken: Field.optional(Scalar.string),
      expiresAt: Field.optional(Scalar.string),
      status: Field.required(Scalar.stringUnion(["authorization_pending", "slow_down", "expired_token", "access_denied", "ok"] as const)),
      tokenType: Field.optional(Scalar.string),
    });
    export const hydrate = MODEL.hydrate.bind(MODEL);
    export const safeHydrate = MODEL.safeHydrate.bind(MODEL);
    export const serialize = MODEL.serialize.bind(MODEL);
  }
  export namespace DeviceStartRequest {
    export const MODEL: Model.Object<DeviceStartRequest> = Model.object<DeviceStartRequest>({
      label: Field.optional(Scalar.string),
      scopes: Field.optional(Model.array(Scalar.string)),
      tokenTtlSeconds: Field.optional(Scalar.number),
    });
    export const hydrate = MODEL.hydrate.bind(MODEL);
    export const safeHydrate = MODEL.safeHydrate.bind(MODEL);
    export const serialize = MODEL.serialize.bind(MODEL);
  }
  export namespace DeviceStartResponse {
    export const MODEL: Model.Object<DeviceStartResponse> = Model.object<DeviceStartResponse>({
      deviceCode: Field.required(Scalar.string),
      expiresInSeconds: Field.required(Scalar.number),
      intervalSeconds: Field.required(Scalar.number),
      userCode: Field.required(Scalar.string),
      verificationUrl: Field.required(Scalar.string),
      verificationUrlComplete: Field.optional(Scalar.string),
    });
    export const hydrate = MODEL.hydrate.bind(MODEL);
    export const safeHydrate = MODEL.safeHydrate.bind(MODEL);
    export const serialize = MODEL.serialize.bind(MODEL);
  }
  export namespace DeviceVerifyRequest {
    export const MODEL: Model.Object<DeviceVerifyRequest> = Model.object<DeviceVerifyRequest>({
      userCode: Field.required(Scalar.string),
    });
    export const hydrate = MODEL.hydrate.bind(MODEL);
    export const safeHydrate = MODEL.safeHydrate.bind(MODEL);
    export const serialize = MODEL.serialize.bind(MODEL);
  }
  export namespace DeviceVerifyResponse {
    export const MODEL: Model.Object<DeviceVerifyResponse> = Model.object<DeviceVerifyResponse>({
      status: Field.required(Scalar.string),
    });
    export const hydrate = MODEL.hydrate.bind(MODEL);
    export const safeHydrate = MODEL.safeHydrate.bind(MODEL);
    export const serialize = MODEL.serialize.bind(MODEL);
  }
  export namespace DeviceAuthErrorResponse {
    export function parse(raw: UntypedJson): DeviceAuthErrorResponse {
      return raw;
    }
  }
  export namespace DevicePollRequest {
    export function parse(raw: UntypedJson): DevicePollRequest {
      return raw;
    }
  }
  export namespace DevicePollResponse {
    export function parse(raw: UntypedJson): DevicePollResponse {
      return raw;
    }
  }
  export namespace DeviceStartRequest {
    export function parse(raw: UntypedJson): DeviceStartRequest {
      return raw;
    }
  }
  export namespace DeviceStartResponse {
    export function parse(raw: UntypedJson): DeviceStartResponse {
      return raw;
    }
  }
  export namespace DeviceVerifyRequest {
    export function parse(raw: UntypedJson): DeviceVerifyRequest {
      return raw;
    }
  }
  export namespace DeviceVerifyResponse {
    export function parse(raw: UntypedJson): DeviceVerifyResponse {
      return raw;
    }
  }
  export type Routes = {
    "/_/auth/device/poll": {
      post: {
        config: {
          body: DevicePollRequest
        }
        response: DevicePollResponse
      }
    }
    "/_/auth/device/start": {
      post: {
        config: {
          body: DeviceStartRequest
        }
        response: DeviceStartResponse
      }
    }
    "/_/auth/device/verify": {
      post: {
        config: {
          body: DeviceVerifyRequest
        }
        response: DeviceVerifyResponse
      }
    }
    "/_/auth/id": {
      get: {
        response: ServiceViewerContext
      }
    }
    "/_/auth/signout": {
      get: {
      }
      post: {
      }
    }
  };
  export const REVIVERS = {
    "/_/auth/device/poll": {
      parse: {
        post: DevicePollResponse.parse,
      },
    },
    "/_/auth/device/start": {
      parse: {
        post: DeviceStartResponse.parse,
      },
    },
    "/_/auth/device/verify": {
      parse: {
        post: DeviceVerifyResponse.parse,
      },
    },
    "/_/auth/id": {
      parse: {
      },
    },
  };
}
